import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { DealDtoKey } from 'api/Clients/Types/DealDto'
import { Link } from 'components/Link'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const introducingBrokerDealsColumn: GridColumn[] = [
  {
    title: 'Trade',
    field: DealDtoKey.ticket,
    show: true,
    width: 100,
    minResizableWidth: 100,
  },
  {
    title: 'Trading Account',
    field: DealDtoKey.tradingAccount,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccount = props.dataItem.tradingAccount
      if (!tradingAccount) {
        return <td />
      }
      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccount.id,
                slug: 'details',
              }),
            }}
          >
            {tradingAccount.name}
          </Link>
        </td>
      )
    },
    resizable: true,
    show: true,
    width: 140,
    minResizableWidth: 140,
  },
  {
    title: 'Client',
    field: DealDtoKey.client,
    cell: (props: GridCellProps) => (
      <td
        className='table-columns__content'
        style={{ color: 'var(--link)', textDecoration: 'underline' }}
      >
        <Link
          to={{
            pathname: generatePath(links.recordsClients.detailsUrl as string, {
              id: props.dataItem.client.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.client.name}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    filter: 'text',
    width: 220,
    minResizableWidth: 220,
  },
  {
    title: 'Instrument',
    field: DealDtoKey.instrument,
    show: true,
    filterable: true,
    filter: 'text',
    className: 'table-columns__content',
    width: 100,
    minResizableWidth: 100,
  },
  {
    title: 'Volume',
    field: DealDtoKey.volume,
    show: true,
    filterable: true,
    className: 'table-columns__number',
    filter: 'text',
    width: 75,
    minResizableWidth: 75,
  },
  {
    title: 'Amount',
    field: DealDtoKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      const { amount } = props.dataItem
      if (amount === 0) {
        return <td className='table-columns__number'>0.00</td>
      }
      if (amount === 1) {
        return <td className='table-columns__number'>1.00</td>
      }
      if (amount > 1) {
        return <td className='table-columns__number'>{amount}</td>
      }
      return (
        <td className='table-columns__number'>{parseFloat(props.dataItem.amount).toFixed(4)}</td>
      )
    },
    show: true,
    filterable: true,
    className: 'table-columns__number',
    filter: 'text',
    width: 85,
    minResizableWidth: 85,
  },
  {
    title: 'Wallet Transaction',
    field: DealDtoKey.walletTransactionReference,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props?.dataItem?.walletTransactionId) {
        return <td />
      }
      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath('/transactions/:id', {
                id: props.dataItem.walletTransactionId,
              }),
            }}
          >
            {props.dataItem.walletTransactionReference}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 180,
    minResizableWidth: 180,
  },
  {
    title: 'Date Imported',
    field: DealDtoKey.createdAt,
    show: true,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.createdAt, true)}</td>
    ),
    filter: 'text',
    minResizableWidth: 120,
    width: 120,
  },
  {
    title: 'Date Closed',
    field: DealDtoKey.tradedAt,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.tradedAt, true)}</td>
    ),
    show: true,
    filter: 'text',
    minResizableWidth: 120,
    width: 120,
  },
  {
    title: 'State',
    field: DealDtoKey.state,
    show: true,
    filterable: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 90,
    minResizableWidth: 90,
  },
  {
    title: 'Entity',
    field: DealDtoKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.tickmillCompany?.id) {
        return <td />
      }
      return (
        <td>
          <CompanyTag id={props.dataItem.tickmillCompany.id}>
            {props.dataItem.tickmillCompany.name}
          </CompanyTag>
        </td>
      )
    },
    show: true,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
]

export default introducingBrokerDealsColumn
